import { toast } from 'sonner'
import { create } from 'zustand'
import { handlePlanError } from '@/components/notify'
import { authKeys } from '@/features/auth/api'
import { queryClient } from '@/lib/query-client'
import type { Me } from '@/types/api'
import { invalidateMedia, patchMediaCache } from '../api'
import { kindLabel } from '../lib/display'
import { ACTIVE_PHASES, UploadManager, type UploadTask } from './manager'
import { httpUploadDeps } from './transport'

/** The app-wide upload manager (one queue for the whole SPA, survives route changes). */
export const uploadManager = new UploadManager(httpUploadDeps, {
  maxFileBytes: () => {
    const me = queryClient.getQueryData<Me | null>(authKeys.me())
    return me?.subscription?.entitlements.limits.upload_bytes?.limit ?? null
  },
  onQuotaExceeded: (error) => void handlePlanError(error),
  onCreated: () => invalidateMedia(queryClient, { folders: false, usage: false }),
  onSettled: (task) => {
    if (task.media) patchMediaCache(queryClient, task.media)
    invalidateMedia(queryClient, { id: task.mediaId ?? undefined })
  },
})

interface UploadUiState {
  tasks: UploadTask[]
  /** Tray minimised to its header. */
  collapsed: boolean
  setCollapsed: (collapsed: boolean) => void
  /** Rendered tray height (px) — toasts stack above it instead of covering it. */
  trayHeight: number
}

export const useUploads = create<UploadUiState>()((set) => ({
  tasks: uploadManager.getSnapshot(),
  collapsed: false,
  setCollapsed: (collapsed) => set({ collapsed }),
  trayHeight: 0,
}))

uploadManager.subscribe(() => useUploads.setState({ tasks: uploadManager.getSnapshot() }))

/** Queue files for upload. Expands the tray so progress is visible. */
export function enqueueUploads(files: Iterable<File> | FileList, target: { folderId?: string | null; replaceOf?: string | null } = {}): UploadTask[] {
  const list = Array.from(files as Iterable<File>).filter((f) => f.size > 0 || f.name)
  const empty = list.filter((f) => f.size === 0)
  if (empty.length) toast.error(empty.length === 1 ? `“${empty[0]!.name}” is empty` : `${empty.length} empty files skipped`)
  const valid = list.filter((f) => f.size > 0)
  if (!valid.length) return []
  useUploads.setState({ collapsed: false })
  return uploadManager.add(valid, target)
}

/** Replace a media's file (new revision). The kind must stay the same. */
export function enqueueReplacement(file: File, media: { id: string; kind: string; name: string }, fileKind: string | null): UploadTask | null {
  if (fileKind !== media.kind) {
    toast.error('Pick a file of the same type', {
      description: `“${media.name}” is ${kindNoun(media.kind)} — a replacement must be one too.`,
    })
    return null
  }
  return enqueueUploads([file], { replaceOf: media.id })[0] ?? null
}

function kindNoun(kind: string): string {
  const label = kindLabel(kind)
  const word = label === label.toUpperCase() ? label : label.toLowerCase()
  return `${/^[aeiou]/i.test(word) || /^(svg|html)/i.test(word) ? 'an' : 'a'} ${word}`
}

export function selectActiveCount(s: UploadUiState): number {
  return s.tasks.filter((t) => ACTIVE_PHASES.has(t.phase)).length
}

/** Overall byte progress across uploads that still transfer bytes (0..1, null when idle). */
export function overallProgress(tasks: readonly UploadTask[]): number | null {
  const moving = tasks.filter((t) => ACTIVE_PHASES.has(t.phase))
  if (!moving.length) return null
  const total = moving.reduce((n, t) => n + t.size, 0)
  if (!total) return 0
  return moving.reduce((n, t) => n + t.uploadedBytes, 0) / total
}
